// The few strings ui.ts writes at run time, per page language. The static lobby text of the localized home
// pages (/tr /pt /es /ru) is translated at build time (src/site/pages.ts); the page says its language in
// <main id="lobby" data-lang="..">. Anything not listed here (server messages, death lines) stays English.
export type Lang = 'en' | 'tr' | 'pt' | 'es' | 'ru';
export const LANGS: Lang[] = ['en', 'tr', 'pt', 'es', 'ru'];
export const isLang = (s: unknown): s is Lang => typeof s === 'string' && (LANGS as string[]).includes(s);

export interface Strings {
  play: string; playOnline: string; playFriends: string; playFree: string;
  beatMe: string; copyRoom: string; copied: string; roomCopied: string; copyFailed: string;
  challenge: string; // {name} {score}
  challengeLabel: string; beaten: string; toGo: string; // toGo: {n}
  share: string; // {score}
  removeAds: string; unlock: string;
}

export const STRINGS: Record<Lang, Strings> = {
  en: {
    play: 'Play', playOnline: 'Play online', playFriends: 'Play with friends', playFree: 'Play free vs bots',
    beatMe: 'Beat my score: copy link', copyRoom: 'Copy room link',
    copied: 'Link copied! Send it to a friend.', roomCopied: 'Room link copied! Friends who open it join your room.',
    copyFailed: 'Copy this link:',
    challenge: '{name} reached {score} mass. Can you beat it?',
    challengeLabel: 'Challenge', beaten: 'beaten!', toGo: '{n} mass to go',
    share: 'I reached {score} mass in CellGame.io. Beat my score!',
    removeAds: 'Remove ads · $5 via Stripe', unlock: 'Unlock · $5 via Stripe',
  },
  tr: {
    play: 'Oyna', playOnline: 'Çevrim içi oyna', playFriends: 'Arkadaşlarla oyna', playFree: 'Botlara karşı ücretsiz oyna',
    beatMe: 'Skorumu geç: bağlantıyı kopyala', copyRoom: 'Oda bağlantısını kopyala',
    copied: 'Bağlantı kopyalandı! Bir arkadaşına gönder.', roomCopied: 'Oda bağlantısı kopyalandı! Açan arkadaşların odana katılır.',
    copyFailed: 'Bu bağlantıyı kopyala:',
    challenge: '{name} {score} kütleye ulaştı. Sen geçebilir misin?',
    challengeLabel: 'Meydan okuma', beaten: 'geçtin!', toGo: '{n} kütle kaldı',
    share: 'CellGame.io\'da {score} kütleye ulaştım. Skorumu geçebilir misin?',
    removeAds: 'Reklamları kaldır · 5 $', unlock: 'Kilidi aç · 5 $',
  },
  pt: {
    play: 'Jogar', playOnline: 'Jogar online', playFriends: 'Jogar com amigos', playFree: 'Jogar grátis contra bots',
    beatMe: 'Supere meu recorde: copiar link', copyRoom: 'Copiar link da sala',
    copied: 'Link copiado! Mande para um amigo.', roomCopied: 'Link da sala copiado! Quem abrir entra na sua sala.',
    copyFailed: 'Copie este link:',
    challenge: '{name} chegou a {score} de massa. Você consegue superar?',
    challengeLabel: 'Desafio', beaten: 'superado!', toGo: 'faltam {n} de massa',
    share: 'Cheguei a {score} de massa no CellGame.io. Supere meu recorde!',
    removeAds: 'Remover anúncios · US$ 5 via Stripe', unlock: 'Desbloquear · US$ 5 via Stripe',
  },
  es: {
    play: 'Jugar', playOnline: 'Jugar online', playFriends: 'Jugar con amigos', playFree: 'Jugar gratis contra bots',
    beatMe: 'Supera mi récord: copiar enlace', copyRoom: 'Copiar enlace de la sala',
    copied: '¡Enlace copiado! Envíaselo a un amigo.', roomCopied: '¡Enlace de la sala copiado! Quien lo abra entra en tu sala.',
    copyFailed: 'Copia este enlace:',
    challenge: '{name} llegó a {score} de masa. ¿Puedes superarlo?',
    challengeLabel: 'Reto', beaten: '¡superado!', toGo: 'te faltan {n} de masa',
    share: 'Llegué a {score} de masa en CellGame.io. ¡Supera mi récord!',
    removeAds: 'Quitar anuncios · 5 US$ con Stripe', unlock: 'Desbloquear · 5 US$ con Stripe',
  },
  ru: {
    play: 'Играть', playOnline: 'Играть онлайн', playFriends: 'Играть с друзьями', playFree: 'Играть бесплатно с ботами',
    beatMe: 'Побей мой рекорд: копировать ссылку', copyRoom: 'Копировать ссылку на комнату',
    copied: 'Ссылка скопирована! Отправь её другу.', roomCopied: 'Ссылка на комнату скопирована! Друзья по ней попадут к тебе.',
    copyFailed: 'Скопируй эту ссылку:',
    challenge: 'Рекорд игрока {name}: {score} массы. Сможешь больше?',
    challengeLabel: 'Вызов', beaten: 'рекорд побит!', toGo: 'осталось {n} массы',
    share: 'Мой рекорд в CellGame.io: {score} массы. Побей его!',
    removeAds: 'Убрать рекламу · 5 $ через Stripe', unlock: 'Открыть всё · 5 $ через Stripe',
  },
};

export const fill = (s: string, vars: Record<string, string | number>) =>
  s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
